import { useEffect, useRef, useState } from 'react'
import { Activity, Lock, RotateCcw, ShieldCheck } from 'lucide-react'
import { Pastille, fmtAge, type Ton } from '../vues/commun.js'
import { messageEchecMutation } from '../api.js'
import { ChampReglage, type ContexteChamps } from './champs.js'
import { ecrireSonde } from './ecriture.js'
import {
  VERITE, demandeStepUp, egal, fmtDateParis, fmtValeur, lireBrouillon, minEffectif, phraseSens,
  type Brouillon, type Portee, type ReglagePublie, type ValeurReglage,
} from './modele.js'

/**
 * UNE LIGNE DE RÉGLAGE : libellé, valeur et unité, « défaut : X » et
 * « Remettre au défaut », la phrase de conséquence, l'aide, la source de
 * vérité (S/G/C/É), « non réglable » si fixé, le pictogramme de step-up si
 * sensible, la surcharge à la portée choisie, et pour un seuil la valeur en
 * direct et la simulation que Sonde fournit (`POST settings/preview`).
 */

export interface PropsLigne {
  s: ReglagePublie
  serveur: ValeurReglage
  brouillon: Brouillon
  /** Modifié par rapport au serveur (ou marqué pour remise au défaut). */
  modifie: boolean
  remis: boolean
  erreur: string | null
  peutModifier: boolean
  portee: Portee | null
  ctx: ContexteChamps
  onChange: (b: Brouillon) => void
  onRemettre: () => void
  onAnnuler: () => void
}

export function LigneReglage(p: PropsLigne) {
  const { s, serveur } = p
  const verite = VERITE[s.truth] ?? { lettre: s.truth, texte: `Source : ${s.truth}` }
  const desactive = s.fixed || !p.peutModifier
  const lu = lireBrouillon(s, p.brouillon)
  const valeurProposee = lu.ok ? lu.value : serveur.value
  const stepUp = p.modifie && (p.remis ? demandeStepUp(s, serveur.value, serveur.default) : demandeStepUp(s, serveur.value, valeurProposee))
  const surcharge = p.portee !== null
  const idTitre = `sv-reg-${s.key.replace(/[^a-z0-9]/gi, '-')}`
  const plancher = minEffectif(s) !== null && s.floor !== null && minEffectif(s) === s.floor ? s.floor : null

  return (
    <li className={`sv-reg-ligne${p.modifie ? ' sv-reg-ligne--modifie' : ''}${s.fixed ? ' sv-reg-ligne--fixe' : ''}`} aria-labelledby={idTitre}>
      <div className="sv-reg-ligne__texte">
        <div className="sv-reg-ligne__titre">
          <span id={idTitre} className="sv-reg-ligne__libelle">{s.label}</span>
          <span className="sv-reg-verite" title={verite.texte} aria-label={verite.texte}>{verite.lettre}</span>
          {s.fixed ? <span className="sv-reg-badge"><Lock size={13} aria-hidden="true" /> non réglable</span> : null}
          {s.sensitive ? (
            <span className="sv-reg-badge sv-reg-badge--stepup" title={s.sensitive === 'on_decrease' ? 'Une baisse demande une confirmation d’identité' : 'Toute modification demande une confirmation d’identité'}>
              <ShieldCheck size={13} aria-hidden="true" /> {s.sensitive === 'on_decrease' ? 'baisse confirmée' : 'confirmation d’identité'}
            </span>
          ) : null}
          {surcharge ? (
            <span className={`sv-reg-badge${serveur.is_default ? '' : ' sv-reg-badge--surcharge'}`}>
              {serveur.is_default ? 'hérité' : 'surchargé ici'}
            </span>
          ) : null}
        </div>
        <p className="sv-reg-ligne__conseq">{s.consequence}</p>
        {s.help && s.help !== s.consequence ? <p className="sv-reg-ligne__aide">{s.help}</p> : null}
        <p className="sv-reg-ligne__meta">
          <span className="sv-mono">{s.key}</span>
          {serveur.updated_at ? <span>modifié le {fmtDateParis(serveur.updated_at)}{serveur.updated_by ? ` par ${serveur.updated_by}` : ''}</span> : null}
          {plancher !== null ? <span>plancher non réglable : {plancher.toLocaleString('fr-FR')}{s.unit ? ` ${s.unit}` : ''}</span> : null}
        </p>
      </div>

      <div className="sv-reg-ligne__valeur">
        <ChampReglage s={s} b={p.brouillon} onChange={p.onChange} desactive={desactive} ctx={p.ctx} />
        {p.erreur ? <p className="sv-reg-erreur" role="alert">{p.erreur}</p> : null}
        <div className="sv-reg-ligne__defaut">
          <span>{surcharge ? 'Sans surcharge' : 'Défaut'} : <strong>{fmtValeur(s, serveur.default)}</strong></span>
          {p.remis ? <span className="sv-reg-remis">sera remis au défaut</span> : null}
          {!desactive && !serveur.is_default && !p.remis ? (
            <button type="button" className="lc-btn lc-btn--ghost" onClick={p.onRemettre}>
              <RotateCcw size={14} aria-hidden="true" /> {surcharge ? 'Retirer la surcharge' : 'Remettre au défaut'}
            </button>
          ) : null}
          {p.modifie ? <button type="button" className="lc-btn lc-btn--ghost" onClick={p.onAnnuler}>Annuler</button> : null}
        </div>
        {stepUp ? <p className="sv-reg-stepup"><ShieldCheck size={14} aria-hidden="true" /> Enregistrer demandera une confirmation d’identité.</p> : null}
        {s.type === 'threshold' && !s.fixed ? <ApercuSeuil s={s} valeur={valeurProposee} actuelle={serveur.value} portee={p.portee} /> : null}
      </div>
    </li>
  )
}

/* ═══════════════ Valeur en direct et simulation d'un seuil ═══════════════ */

interface ApercuDoc {
  unit: string | null
  direction: 'above' | 'below' | null
  live: { entity_key: string; label: string; value: number | null; at: string | null; data_age_s: number | null; level: 'ok' | 'warn' | 'crit' | null } | null
  nights7: { night: string; value: number | null }[]
  simulable: boolean
  reason: string | null
  delta_fires: number | null
  verdict: string
}
const estApercu = (x: unknown): x is ApercuDoc => typeof x === 'object' && x !== null && Array.isArray((x as ApercuDoc).nights7)
  && typeof (x as ApercuDoc).verdict === 'string'

const TON_APERCU: Record<string, Ton> = { ok: 'ok', warn: 'warn', crit: 'err' }
const LIBELLE_APERCU: Record<string, string> = { ok: 'sous le seuil', warn: 'avertissement', crit: 'critique' }

function ApercuSeuil({ s, valeur, actuelle, portee }: { s: ReglagePublie; valeur: unknown; actuelle: unknown; portee: Portee | null }) {
  const [ouvert, setOuvert] = useState(false)
  const [doc, setDoc] = useState<ApercuDoc | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)
  const cle = JSON.stringify([valeur, portee])
  // La valeur change d'identité à chaque rendu du parent : seul le texte `cle` déclenche une relecture.
  const demande = useRef({ key: s.key, valeur, portee })
  demande.current = { key: s.key, valeur, portee }
  const numero = useRef(0)
  const premiere = useRef(true)

  useEffect(() => {
    if (!ouvert) return
    const n = ++numero.current
    const minuteur = setTimeout(() => {
      const d = demande.current
      setEnCours(true)
      void ecrireSonde('POST', 'settings/preview', {
        key: d.key, value: d.valeur, days: 30, ...(d.portee ? { scope: { kind: d.portee.kind, ref: d.portee.ref } } : {}),
      }).then((r) => {
        if (n !== numero.current) return
        if (r.status >= 200 && r.status < 300 && estApercu(r.data)) { setDoc(r.data); setErreur(null) }
        else { setErreur(r.status >= 300 ? messageEchecMutation(r.status, r.data, 'aucun aperçu') : 'Réponse inattendue : aucun chiffre plutôt qu’un chiffre faux.') }
      }).catch(() => { if (n === numero.current) setErreur('Gestion n’a pas répondu.') })
        .finally(() => { if (n === numero.current) setEnCours(false) })
    }, premiere.current ? 0 : 900)
    premiere.current = false
    return () => clearTimeout(minuteur)
  }, [ouvert, cle])

  if (!ouvert) {
    return (
      <button type="button" className="lc-btn lc-btn--ghost sv-reg-apercu__ouvrir" onClick={() => setOuvert(true)}>
        <Activity size={14} aria-hidden="true" /> Valeur en direct et simulation
      </button>
    )
  }
  const seuil = (valeur ?? actuelle) as { warn?: number | null; crit?: number | null } | null
  const live = doc?.live ?? null
  const u = doc?.unit ?? s.unit
  return (
    <div className="sv-reg-apercu" aria-live="polite">
      {erreur ? <p className="sv-reg-apercu__gris">{erreur}</p> : null}
      {!doc && !erreur ? <p className="sv-reg-apercu__gris">Lecture de Sonde…</p> : null}
      {doc ? (
        <>
          <div className="sv-reg-apercu__direct">
            <span className="sv-reg-apercu__titre">En direct</span>
            {live && live.value !== null ? (
              <>
                <strong className="sv-reg-apercu__valeur">{live.value.toLocaleString('fr-FR')}{u ? ` ${u}` : ''}</strong>
                <Pastille ton={live.level ? TON_APERCU[live.level] : 'gris'}>{live.level ? LIBELLE_APERCU[live.level] : 'niveau inconnu'}</Pastille>
                <span className="sv-reg-apercu__src">{live.label} · {fmtAge(live.data_age_s)}</span>
              </>
            ) : <span className="sv-reg-apercu__gris">— aucune mesure récente pour ce seuil</span>}
          </div>
          {doc.nights7.length > 0 ? <MiniCourbe points={doc.nights7} warn={seuil?.warn ?? null} crit={seuil?.crit ?? null} unite={u} sens={doc.direction ?? s.direction} /> : null}
          <p className="sv-reg-apercu__verdict">
            {doc.simulable ? doc.verdict : (doc.reason ?? doc.verdict)}
            {doc.simulable && doc.delta_fires !== null && doc.delta_fires !== 0 && !egal(valeur, actuelle)
              ? ` (${doc.delta_fires > 0 ? '+' : ''}${doc.delta_fires} déclenchement${Math.abs(doc.delta_fires) > 1 ? 's' : ''} sur 30 nuits)` : ''}
          </p>
          <p className="lc-hint">Source : Sonde{enCours ? ' · relecture…' : ''}</p>
        </>
      ) : null}
    </div>
  )
}

/** Pire valeur des 7 dernières nuits, avec les lignes du seuil. Une nuit sans donnée est un trou, jamais un zéro. */
function MiniCourbe({ points, warn, crit, unite, sens }: {
  points: { night: string; value: number | null }[]; warn: number | null; crit: number | null; unite: string | null; sens: 'above' | 'below' | null
}) {
  const vals = points.map((p) => p.value).filter((v): v is number => v !== null)
  const toutes = [...vals, ...(warn !== null ? [warn] : []), ...(crit !== null ? [crit] : [])]
  if (toutes.length === 0) return <p className="sv-reg-apercu__gris">7 dernières nuits : aucune donnée.</p>
  const lo = Math.min(...toutes), hi = Math.max(...toutes)
  const marge = (hi - lo) * 0.12 || Math.abs(hi) * 0.1 || 1
  const y0 = lo - marge, y1 = hi + marge
  const W = 280, H = 64
  const x = (i: number) => points.length === 1 ? W / 2 : (i / (points.length - 1)) * W
  const y = (v: number) => H - ((v - y0) / (y1 - y0)) * H
  const segments: string[] = []
  let courant = ''
  points.forEach((p, i) => {
    if (p.value === null) { if (courant) segments.push(courant); courant = ''; return }
    courant += `${courant ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`
  })
  if (courant) segments.push(courant)
  const resume = points.map((p) => `${p.night.slice(8, 10)}/${p.night.slice(5, 7)} : ${p.value === null ? '—' : `${p.value.toLocaleString('fr-FR')}${unite ? ` ${unite}` : ''}`}`).join(', ')
  return (
    <figure className="sv-reg-courbe">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={`Pire valeur des 7 dernières nuits, alerte ${phraseSens(sens)} du seuil : ${resume}`}>
        {warn !== null ? <line className="sv-reg-courbe__warn" x1={0} x2={W} y1={y(warn)} y2={y(warn)} vectorEffect="non-scaling-stroke" /> : null}
        {crit !== null ? <line className="sv-reg-courbe__crit" x1={0} x2={W} y1={y(crit)} y2={y(crit)} vectorEffect="non-scaling-stroke" /> : null}
        {segments.map((d, i) => <path key={i} className="sv-reg-courbe__ligne" d={d} vectorEffect="non-scaling-stroke" />)}
        {points.map((p, i) => p.value === null ? null : (
          <circle key={i} className="sv-reg-courbe__point" cx={x(i)} cy={y(p.value)} r={2.5} vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      <figcaption className="sv-reg-courbe__legende">
        <span>{points[0] ? `${points[0].night.slice(8, 10)}/${points[0].night.slice(5, 7)}` : ''}</span>
        <span>7 dernières nuits (pire valeur)</span>
        <span>{points.length > 1 ? `${points[points.length - 1]!.night.slice(8, 10)}/${points[points.length - 1]!.night.slice(5, 7)}` : ''}</span>
      </figcaption>
    </figure>
  )
}
